// @ts-nocheck
import { useCallback, useEffect, useState } from 'react';
import { getWorkbench, initWorkbench, patchAdjustment } from '../api/workbenchApi';
import { applyOptimisticStatusChange } from './optimistic';

/**
 * Bootstrap + state management for one workbench engagement (= one mapping_session).
 *
 * Lifecycle:
 *   1. Try GET /qoe/workbench/{id}.
 *   2. If 404 or empty, call POST /init to seed from the session's qoe_report.
 *   3. Expose `bootstrap`, `loading`, `error`, plus `refresh`, `mutate`,
 *      and `savingCount` (number of in-flight PATCH calls).
 *
 * `mutate` is optimistic: it patches the bootstrap synchronously with the
 * intended status change so the EBITDA Waterfall + KPIs update on the same
 * tick the user clicks. The PATCH call fires in the background and the
 * authoritative server response replaces local state when it arrives.
 *
 * On error we refetch the whole bootstrap to recover — never leaves the UI
 * in an inconsistent optimistic state.
 */
export default function useWorkbench(mappingSessionId) {
  const [bootstrap, setBootstrap] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingCount, setSavingCount] = useState(0);

  const load = useCallback(async () => {
    if (!mappingSessionId) {
      setBootstrap(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let data = await getWorkbench(mappingSessionId);
      const empty = !data?.periods?.length || !data?.adjustments?.length;
      if (empty) {
        data = await initWorkbench(mappingSessionId);
      }
      setBootstrap(data);
    } catch (err) {
      if (err.status === 404) {
        try {
          const seeded = await initWorkbench(mappingSessionId);
          setBootstrap(seeded);
          setError(null);
        } catch (initErr) {
          setError(initErr.message);
        }
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [mappingSessionId]);

  useEffect(() => {
    load();
  }, [load]);

  const reseed = useCallback(async () => {
    if (!mappingSessionId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await initWorkbench(mappingSessionId);
      setBootstrap(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [mappingSessionId]);

  const mutate = useCallback(
    async (adjustmentId, patchBody) => {
      // Apply optimistic update synchronously so the waterfall & KPIs reflect
      // the change on the same render tick the user clicked.
      if (patchBody?.status) {
        setBootstrap((prev) =>
          applyOptimisticStatusChange(prev, adjustmentId, patchBody.status)
        );
      }
      setSavingCount((c) => c + 1);
      try {
        const data = await patchAdjustment(adjustmentId, patchBody);
        // Server is the source of truth — replace local state with its
        // response, which also has freshly-computed impact_by_period for
        // tier-2 rows that the optimistic patch couldn't predict.
        setBootstrap(data);
        return data;
      } catch (err) {
        // Recover from any optimistic drift by refetching from the server.
        load();
        throw err;
      } finally {
        setSavingCount((c) => Math.max(0, c - 1));
      }
    },
    [load]
  );

  return {
    bootstrap,
    loading,
    error,
    savingCount,
    refresh: load,
    reseed,
    mutate,
  };
}
